/* =====================================================================
 * StorageSidebar — Finder형 좌측 트리
 * - 팀 스페이스: 팀별 모드(에이전트~음성 기록)
 * - 다이렉트 메시지: 팀별 DM 업로드 파일
 * - 워크스페이스: 개인 모드(에이전트~음성 기록)
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Folder, Menu, Sparkles } from 'lucide-react';
import type { WorkspaceItem, WorkspaceMode } from '@auw/workspace-utils';
import type { TeamInfo } from '@auw/api-client';
import { WORKSPACE_MODES, WORKSPACE_ORDER } from '@auw/workspace-utils';
import { workspaceModeToAgentProfileId } from '@auw/i18n';
import { useSkillStore } from '@auw/stores';
import { resolveSpecializedAgents, useTenant } from '@auw/tenant';
import { useFeatureFlags, type FeatureId } from '@auw/feature-flags';
import { getSpecializedStorageComponents } from '../../lib/specializedStorageSlot';
import {
  SidebarPane,
  SidebarHeader,
  SidebarHeaderLeft,
  SidebarTitle,
  SidebarToggleBtn,
  SidebarTreeWrap,
  TreeNodeLabel,
  SectionDivider,
  SidebarSection,
  SidebarSectionHeader,
  SectionLabel,
  SectionChevron,
  TreeNodeRow,
  ModeAvatarWrap,
  ModeAvatarCircle,
  ModeAvatarImage,
  ModeAvatarDot,
  TeamFolderAvatar,
  SkillAvatar,
  EmptyHint,
} from './storage.styles';

export interface SidebarSelection {
  section: 'team-space' | 'direct-message' | 'workspace' | 'skills' | 'specialized';
  teamId: string | null;
  mode: WorkspaceMode | null;
  /** specialized 섹션에서만 사용. 매니페스트 `specializedAgents[].id`. */
  specializedAgentId?: string;
  /** specialized 트리 — 선택된 프로젝트 id (CAD 와 같은 도메인). */
  specializedProjectId?: string | null;
  /** specialized 트리 — 선택된 폴더 id (없으면 프로젝트 루트). */
  specializedFolderId?: string | null;
}

interface Props {
  teams: TeamInfo[];
  teamWorkspaceItems: Record<string, WorkspaceItem[]>;
  workspaceItems: WorkspaceItem[];
  selection: SidebarSelection;
  onSelect: (sel: SidebarSelection) => void;
  pageSidebarOpen: boolean;
  onTogglePageSidebar: () => void;
  desktopHidden?: boolean;
}

/**
 * Default Finder-style mode order rendered inside both the team-space
 * and the personal workspace sections. The actual list rendered at
 * runtime is `finderModes` below — this base array is filtered by the
 * tenant manifest so a tenant that only ships e.g. `mode.agent` does
 * not get phantom "라이브 에이전트 / 문서 에이전트 / 브레인 노트"
 * rows pointing to disabled modes.
 */
const FINDER_MODES_BASE: WorkspaceMode[] = WORKSPACE_ORDER;

export const StorageSidebar: React.FC<Props> = ({
  teams,
  selection,
  onSelect,
  pageSidebarOpen,
  onTogglePageSidebar,
  desktopHidden,
}) => {
  const { t } = useTranslation('common');
  const agentPictures: Record<string, string | null> = {};
  const [avatarErrors, setAvatarErrors] = useState<Record<string, boolean>>({});

  const skills = useSkillStore((s) => s.skills);
  const fetchSkills = useSkillStore((s) => s.fetchSkills);
  /* P11-5f: each storage section maps to a `mode.storage.*` flag so a
   * tenant can hide e.g. the Direct Message column without the team
   * APIs falling apart.
   *
   * Each `mode.storage.*` toggle is AND-combined with the upstream
   * tenant flag that controls whether the underlying surface even
   * exists in this build. This keeps the storage sidebar coherent
   * with the rest of the manifest without forcing every tenant to
   * mirror toggles in two places: turning off `mode.message` (DMs)
   * or `mode.skill-create` (skills) in the manifest automatically
   * removes the matching storage column too, even if the tenant
   * left the legacy `mode.storage.*` flag on. The reverse — leaving
   * the upstream surface on but flipping `mode.storage.*` off — is
   * still honoured, so the manifest can hide a column for storage
   * UX reasons while keeping the feature itself reachable elsewhere.
   *
   * Mapping:
   *   - team-space  ← mode.storage.teamFiles    & workspace.collab.groupChat
   *   - direct-msg  ← mode.storage.dmFiles      & mode.message
   *   - workspace   ← mode.storage.personalFiles                (no
   *                  upstream pair: personal files are meaningful
   *                  whenever the storage mode itself is on)
   *   - skills      ← mode.storage.skillsNav    & mode.agent.skillBrowse
   *
   * NOTE on the skills pair: the storage "스킬" section is a *browser*
   * (list + click-to-select), not a creation surface, so it is paired
   * with `mode.agent.skillBrowse` (whether the user can pick existing
   * skills) rather than `mode.skill-create` (whether the user can
   * author new ones). On-prem tenants like Samsung that turn off
   * skill creation but keep skill browsing on (so the agent can use
   * IT-published skills) still get the storage column. Tenants that
   * want to fully hide the skills concept must turn off
   * `mode.agent.skillBrowse` (or `mode.storage.skillsNav` for a
   * storage-only override).
   */
  const featureFlags = useFeatureFlags();
  const groupChatEnabled = featureFlags.isEnabled('workspace.collab.groupChat');
  const messageModeEnabled = featureFlags.isEnabled('mode.message');
  const skillBrowseEnabled = featureFlags.isEnabled('mode.agent.skillBrowse');

  const teamFilesEnabled =
    featureFlags.isEnabled('mode.storage.teamFiles') && groupChatEnabled;
  const personalFilesEnabled = featureFlags.isEnabled('mode.storage.personalFiles');
  const dmFilesEnabled =
    featureFlags.isEnabled('mode.storage.dmFiles') && messageModeEnabled;
  const skillsNavEnabled =
    featureFlags.isEnabled('mode.storage.skillsNav') && skillBrowseEnabled;

  /**
   * Filter the Finder mode list down to the modes the tenant actually
   * ships. WORKSPACE_ORDER hard-codes `agent / live-agent / doc-agent
   * / document`, but a manifest like Samsung's only enables
   * `mode.agent`, so without this filter the storage tree shows three
   * rows that deep-link into placeholders. We use `useMemo` so the
   * derived array keeps a stable identity across re-renders (the
   * FeatureFlags context is stable for the lifetime of the manifest,
   * which is set at build time, so this collapses to a one-shot
   * computation in practice).
   */
  const finderModes = useMemo(
    () =>
      FINDER_MODES_BASE.filter((mode) =>
        featureFlags.isEnabled(`mode.${mode}` as FeatureId),
      ),
    [featureFlags],
  );

  useEffect(() => {
    if (!skillsNavEnabled) return;
    void fetchSkills();
  }, [fetchSkills, skillsNavEnabled]);

  const markAvatarError = useCallback((key: string) => {
    setAvatarErrors((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  }, []);

  const [openSections, setOpenSections] = useState<Set<string>>(
    new Set(['team-space', 'direct-message', 'workspace', 'skills', 'specialized']),
  );

  const tenant = useTenant();
  const specializedAgents = useMemo(
    () => resolveSpecializedAgents(tenant),
    [tenant],
  );
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());

  const toggleSection = useCallback((key: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleTeamExpand = useCallback((teamId: string) => {
    setExpandedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  }, []);

  return (
    <SidebarPane $hidden={desktopHidden}>
      <SidebarHeader>
        <SidebarHeaderLeft>
          <SidebarTitle>{t('nav.storage')}</SidebarTitle>
        </SidebarHeaderLeft>
        <SidebarToggleBtn
          type="button"
          onClick={onTogglePageSidebar}
          title={pageSidebarOpen ? t('storage.collapseSidebar') : t('storage.expandSidebar')}
        >
          <Menu size={16} />
        </SidebarToggleBtn>
      </SidebarHeader>

      <SidebarTreeWrap>
        {teamFilesEnabled && (
        <SidebarSection>
          <SidebarSectionHeader
            onClick={() => {
              toggleSection('team-space');
              if (teams[0]) {
                onSelect({
                  section: 'team-space',
                  teamId: teams[0].team_id,
                  mode: selection.mode ?? 'agent',
                });
              }
            }}
          >
            <SectionChevron $open={openSections.has('team-space')}>
              <ChevronRight size={14} />
            </SectionChevron>
            <SectionLabel>{t('storage.teamSpace')}</SectionLabel>
          </SidebarSectionHeader>

          {openSections.has('team-space') && (
            teams.length === 0 ? (
              <EmptyHint>{t('storage.noTeams')}</EmptyHint>
            ) : (
              teams.map((team) => {
                const expanded = expandedTeams.has(team.team_id);
                const isTeamRootActive =
                  selection.section === 'team-space'
                  && selection.teamId === team.team_id
                  && selection.mode === null;

                return (
                  <React.Fragment key={team.team_id}>
                    <TreeNodeRow
                      $depth={1}
                      $active={isTeamRootActive}
                      onClick={() =>
                        onSelect({
                          section: 'team-space',
                          teamId: team.team_id,
                          mode: null,
                        })
                      }
                    >
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTeamExpand(team.team_id);
                        }}
                        style={{ display: 'flex', alignItems: 'center' }}
                      >
                        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </span>
                      <TeamFolderAvatar>
                        <Folder size={12} />
                      </TeamFolderAvatar>
                      <TreeNodeLabel>{team.name}</TreeNodeLabel>
                    </TreeNodeRow>

                    {expanded && finderModes.map((mode) => {
                      const modeInfo = WORKSPACE_MODES[mode];
                      const profileId = workspaceModeToAgentProfileId(mode);
                      const customPic = profileId ? agentPictures[profileId] : undefined;
                      const avatarKey = `${mode}:${customPic || 'none'}`;
                      const showCustom = !!customPic && !avatarErrors[avatarKey];
                      const active =
                        selection.section === 'team-space'
                        && selection.teamId === team.team_id
                        && selection.mode === mode;
                      return (
                        <TreeNodeRow
                          key={`${team.team_id}:${mode}`}
                          $depth={2}
                          $active={active}
                          onClick={() =>
                            onSelect({
                              section: 'team-space',
                              teamId: team.team_id,
                              mode,
                            })
                          }
                        >
                          <span style={{ width: 14 }} />
                          <ModeAvatarWrap>
                            <ModeAvatarCircle>
                              {showCustom ? (
                                <ModeAvatarImage
                                  src={customPic}
                                  alt={t(modeInfo.labelKey)}
                                  referrerPolicy="no-referrer"
                                  onError={() => markAvatarError(avatarKey)}
                                />
                              ) : (
                                modeInfo.letter
                              )}
                            </ModeAvatarCircle>
                            <ModeAvatarDot $color={modeInfo.color} />
                          </ModeAvatarWrap>
                          <TreeNodeLabel>{t(modeInfo.labelKey)}</TreeNodeLabel>
                        </TreeNodeRow>
                      );
                    })}
                  </React.Fragment>
                );
              })
            )
          )}
        </SidebarSection>
        )}

        {teamFilesEnabled && dmFilesEnabled && <SectionDivider />}

        {dmFilesEnabled && (
        <SidebarSection>
          <SidebarSectionHeader
            onClick={() => {
              toggleSection('direct-message');
              if (teams[0]) {
                onSelect({
                  section: 'direct-message',
                  teamId: teams[0].team_id,
                  mode: null,
                });
              }
            }}
          >
            <SectionChevron $open={openSections.has('direct-message')}>
              <ChevronRight size={14} />
            </SectionChevron>
            <SectionLabel>{t('storage.directMessage')}</SectionLabel>
          </SidebarSectionHeader>

          {openSections.has('direct-message') && (
            teams.length === 0 ? (
              <EmptyHint>{t('storage.noTeams')}</EmptyHint>
            ) : (
              teams.map((team) => {
                const active =
                  selection.section === 'direct-message'
                  && selection.teamId === team.team_id;

                return (
                  <TreeNodeRow
                    key={`dm:${team.team_id}`}
                    $depth={1}
                    $active={active}
                    onClick={() =>
                      onSelect({
                        section: 'direct-message',
                        teamId: team.team_id,
                        mode: null,
                      })
                    }
                  >
                    <span style={{ width: 14 }} />
                    <TeamFolderAvatar>
                      <Folder size={12} />
                    </TeamFolderAvatar>
                    <TreeNodeLabel>{team.name}</TreeNodeLabel>
                  </TreeNodeRow>
                );
              })
            )
          )}
        </SidebarSection>
        )}

        {(teamFilesEnabled || dmFilesEnabled) && personalFilesEnabled && <SectionDivider />}

        {personalFilesEnabled && (
        <SidebarSection>
          <SidebarSectionHeader
            onClick={() => {
              toggleSection('workspace');
              onSelect({
                section: 'workspace',
                teamId: null,
                mode: selection.mode ?? 'agent',
              });
            }}
          >
            <SectionChevron $open={openSections.has('workspace')}>
              <ChevronRight size={14} />
            </SectionChevron>
            <SectionLabel>{t('sidebar.workspace')}</SectionLabel>
          </SidebarSectionHeader>

          {openSections.has('workspace') && (
            finderModes.map((mode) => {
              const modeInfo = WORKSPACE_MODES[mode];
              const profileId = workspaceModeToAgentProfileId(mode);
              const customPic = profileId ? agentPictures[profileId] : undefined;
              const avatarKey = `${mode}:${customPic || 'none'}`;
              const showCustom = !!customPic && !avatarErrors[avatarKey];
              const ModeTree = getSpecializedStorageComponents(mode)?.Tree ?? null;
              const active =
                selection.section === 'workspace'
                && selection.mode === mode
                && (!ModeTree || !selection.specializedProjectId);
              return (
                <React.Fragment key={`workspace:${mode}`}>
                  <TreeNodeRow
                    $depth={1}
                    $active={active}
                    onClick={() =>
                      onSelect({
                        section: 'workspace',
                        teamId: null,
                        mode,
                        specializedProjectId: null,
                        specializedFolderId: null,
                      })
                    }
                  >
                    <span style={{ width: 14 }} />
                    <ModeAvatarWrap>
                      <ModeAvatarCircle>
                        {showCustom ? (
                          <ModeAvatarImage
                            src={customPic}
                            alt={t(modeInfo.labelKey)}
                            referrerPolicy="no-referrer"
                            onError={() => markAvatarError(avatarKey)}
                          />
                        ) : (
                          modeInfo.letter
                        )}
                      </ModeAvatarCircle>
                      <ModeAvatarDot $color={modeInfo.color} />
                    </ModeAvatarWrap>
                    <TreeNodeLabel>{t(modeInfo.labelKey)}</TreeNodeLabel>
                  </TreeNodeRow>
                  {ModeTree && <ModeTree selection={selection} onSelect={onSelect} />}
                </React.Fragment>
              );
            })
          )}
        </SidebarSection>
        )}

        {(teamFilesEnabled || dmFilesEnabled || personalFilesEnabled) && skillsNavEnabled && <SectionDivider />}

        {skillsNavEnabled && (
        <SidebarSection>
          <SidebarSectionHeader
            onClick={() => {
              toggleSection('skills');
              onSelect({ section: 'skills', teamId: null, mode: null });
            }}
          >
            <SectionChevron $open={openSections.has('skills')}>
              <ChevronRight size={14} />
            </SectionChevron>
            <SectionLabel>{t('storage.skills')}</SectionLabel>
          </SidebarSectionHeader>

          {openSections.has('skills') && (
            skills.length === 0 ? (
              <EmptyHint>{t('storage.noSkills')}</EmptyHint>
            ) : (
              skills.map((skill) => (
                <TreeNodeRow
                  key={`skill:${skill.skill_id}`}
                  $depth={1}
                  $active={false}
                  onClick={() =>
                    onSelect({
                      section: 'skills',
                      teamId: null,
                      mode: null,
                    })
                  }
                >
                  <span style={{ width: 14 }} />
                  <SkillAvatar>
                    <Sparkles size={12} />
                  </SkillAvatar>
                  <TreeNodeLabel>{skill.name}</TreeNodeLabel>
                </TreeNodeRow>
              ))
            )
          )}
        </SidebarSection>
        )}

        {/* ---- "전문 에이전트" 섹션 ----
         *
         * skills 섹션 바로 아래에 노출. 각 specialized 에이전트는 자체
         * 트리(예: 프로젝트/폴더) 를 그릴 수 있도록 호스트가 DI slot
         * (`specializedStorageSlot`) 에 Tree 컴포넌트를 등록하면 그
         * 컴포넌트가 children 자리에 렌더된다. Tree 컴포넌트는 좌측
         * 사이드의 한 노드를 클릭하면 `onSelect` 콜백으로 selection 을
         * 갱신해 메인 영역과 동기화. */}
        {specializedAgents.length > 0 && (
          <>
            <SectionDivider />
            <SidebarSection>
              <SidebarSectionHeader onClick={() => toggleSection('specialized')}>
                <SectionChevron $open={openSections.has('specialized')}>
                  <ChevronRight size={14} />
                </SectionChevron>
                <SectionLabel>{t('sidebar.specializedAgents')}</SectionLabel>
              </SidebarSectionHeader>

              {openSections.has('specialized') &&
                specializedAgents.map((agent) => {
                  const isAgentActive =
                    selection.section === 'specialized'
                    && selection.specializedAgentId === agent.id;
                  const label = agent.labelKey ? t(agent.labelKey) : agent.label;
                  const TreeComponent =
                    getSpecializedStorageComponents(agent.id)?.Tree ?? null;
                  return (
                    <React.Fragment key={`specialized:${agent.id}`}>
                      <TreeNodeRow
                        $depth={1}
                        $active={isAgentActive && !selection.specializedProjectId}
                        onClick={() =>
                          onSelect({
                            section: 'specialized',
                            teamId: null,
                            mode: null,
                            specializedAgentId: agent.id,
                            specializedProjectId: null,
                            specializedFolderId: null,
                          })
                        }
                      >
                        <span style={{ width: 14 }} />
                        <SkillAvatar>
                          <Sparkles size={12} />
                        </SkillAvatar>
                        <TreeNodeLabel>{label}</TreeNodeLabel>
                      </TreeNodeRow>
                      {TreeComponent && (
                        <TreeComponent selection={selection} onSelect={onSelect} />
                      )}
                    </React.Fragment>
                  );
                })}
            </SidebarSection>
          </>
        )}
      </SidebarTreeWrap>
    </SidebarPane>
  );
};
